import React, { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, IconButton, Tab, Tabs, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import CreatorGridService from "../../../creator/service/CreatorGridService";
import CronExpressionBuilder, { validateCronExpression } from "../../components/CronExpressionBuilder";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import BedrockIdeaService from "./BedrockIdeaService";
import Logger from "../../../services/Logger";
import BrTabIdea from "./BrTabIdea";
import BrTabLog from "./BrTabLog";
import BrTabTopics from "./BrTabTopics";

const DEFAULT_CRON = "0 0 0 * * ?";

export default function BedrockIdeaFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [loading, setLoading] = useState(isEdit);
    const [formData, setFormData] = useState({
        enabled: false,
        title: "",
        cron: DEFAULT_CRON,
        idea: "",
        isKids: false,
        genHiddenWordsearch: false,
        genWordWheel: false,
        genStandaloneImage: false,
        genWordSearch9: false,
        imageStyle: "",
        roundRobin: false,
        tags: [],
        exhausted: false,
        exhaustedAt: null
    });
    const [availableTags, setAvailableTags] = useState([]);
    const [loadingTags, setLoadingTags] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState("info");
    const [message, setMessage] = useState("");

    const [tabValue, setTabValue] = useState(0);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [genHiddenWordsearchLoading, setGenHiddenWordsearchLoading] = useState(false);
    const [genWordWheelLoading, setGenWordWheelLoading] = useState(false);
    const [genStandaloneImageLoading, setGenStandaloneImageLoading] = useState(false);
    const [genWordSearch9Loading, setGenWordSearch9Loading] = useState(false);
    const [qualityStats, setQualityStats] = useState({
        standaloneImagesCreated: 0,
        imagesRejected: 0,
        rejectedImages: [],
    });

    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;
        const fetchIdea = async () => {
            setLoading(true);
            const result = await BedrockIdeaService.getIdea(id);
            setLoading(false);
            if (cancelled) return;
            if (result.success && result.data) {
                const d = result.data;
                setFormData({
                    enabled: d.enabled || false,
                    title: d.title || "",
                    cron: d.cron || DEFAULT_CRON,
                    idea: d.idea || "",
                    isKids: d.isKids || false,
                    genHiddenWordsearch: d.genHiddenWordsearch || false,
                    genWordWheel: d.genWordWheel || false,
                    genStandaloneImage: d.genStandaloneImage || false,
                    genWordSearch9: d.genWordSearch9 || false,
                    imageStyle: d.imageStyle || "",
                    roundRobin: d.roundRobin || false,
                    tags: Array.isArray(d.tags) ? d.tags : [],
                    exhausted: d.exhausted || false,
                    exhaustedAt: d.exhaustedAt || null
                });
                setQualityStats({
                    standaloneImagesCreated: Number(d.standaloneImagesCreated) || 0,
                    imagesRejected: Number(d.imagesRejected) || 0,
                    rejectedImages: Array.isArray(d.rejectedImages) ? d.rejectedImages : [],
                });
            } else {
                setMessage(result.error || "Failed to load idea");
                setSeverity("error");
                setOpenSnackbar(true);
            }
        };
        fetchIdea();
        return () => { cancelled = true; };
    }, [id, isEdit]);

    useEffect(() => {
        const fetchTags = async () => {
            setLoadingTags(true);
            try {
                const tags = await CreatorGridService.getGridTags("english");
                setAvailableTags(Array.isArray(tags) ? tags : []);
            } catch (error) {
                Logger.error("Error fetching tags:", error);
                setAvailableTags([]);
            } finally {
                setLoadingTags(false);
            }
        };
        fetchTags();
    }, []);

    const handleFormChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
            ...(name === "exhausted" && checked ? { roundRobin: false } : {}),
        }));
    };

    const handleGenStandaloneImageChange = async (event) => {
        const checked = event.target.checked;
        const previous = formData.genStandaloneImage;
        setFormData((prev) => ({
            ...prev,
            genStandaloneImage: checked,
            genWordWheel: checked ? false : prev.genWordWheel,
            genHiddenWordsearch: checked ? false : prev.genHiddenWordsearch,
            genWordSearch9: checked ? false : prev.genWordSearch9,
        }));

        if (!isEdit) return;

        setGenStandaloneImageLoading(true);
        const result = await BedrockIdeaService.updateIdea(id, {
            genStandaloneImage: checked,
            ...(checked ? { genWordWheel: false, genHiddenWordsearch: false, genWordSearch9: false } : {}),
        });
        setGenStandaloneImageLoading(false);

        if (result.success) {
            if (result.data) {
                setFormData((prev) => ({
                    ...prev,
                    genStandaloneImage: Boolean(result.data.genStandaloneImage),
                    genWordWheel: Boolean(result.data.genWordWheel),
                    genHiddenWordsearch: Boolean(result.data.genHiddenWordsearch),
                    genWordSearch9: Boolean(result.data.genWordSearch9),
                }));
            }
            handleSnackBar(
                "success",
                checked ? "Standalone image generation enabled" : "Standalone image generation disabled"
            );
        } else {
            setFormData((prev) => ({ ...prev, genStandaloneImage: previous }));
            handleSnackBar("error", result.error || "Failed to update standalone image flag");
        }
    };

    const handleGenWordWheelChange = async (event) => {
        const checked = event.target.checked;
        if (formData.genStandaloneImage && checked) {
            return;
        }
        const previous = formData.genWordWheel;
        setFormData((prev) => ({
            ...prev,
            genWordWheel: checked,
            genHiddenWordsearch: checked ? false : prev.genHiddenWordsearch,
            genStandaloneImage: checked ? false : prev.genStandaloneImage,
            genWordSearch9: checked ? false : prev.genWordSearch9,
        }));

        if (!isEdit) return;

        setGenWordWheelLoading(true);
        const result = await BedrockIdeaService.updateIdea(id, {
            genWordWheel: checked,
            ...(checked ? { genHiddenWordsearch: false, genStandaloneImage: false, genWordSearch9: false } : {}),
        });
        setGenWordWheelLoading(false);

        if (result.success) {
            if (result.data) {
                setFormData((prev) => ({
                    ...prev,
                    genWordWheel: Boolean(result.data.genWordWheel),
                    genHiddenWordsearch: Boolean(result.data.genHiddenWordsearch),
                    genStandaloneImage: Boolean(result.data.genStandaloneImage),
                    genWordSearch9: Boolean(result.data.genWordSearch9),
                }));
            }
            handleSnackBar(
                "success",
                checked ? "Word Wheel generation enabled" : "Word Wheel generation disabled"
            );
        } else {
            setFormData((prev) => ({ ...prev, genWordWheel: previous }));
            handleSnackBar("error", result.error || "Failed to update Word Wheel flag");
        }
    };

    const handleGenHiddenWordsearchChange = async (event) => {
        const checked = event.target.checked;
        if ((formData.genWordWheel || formData.genStandaloneImage || formData.genWordSearch9) && checked) {
            return;
        }
        const previous = formData.genHiddenWordsearch;
        setFormData((prev) => ({
            ...prev,
            genHiddenWordsearch: checked,
            genWordSearch9: checked ? false : prev.genWordSearch9,
        }));

        if (!isEdit) return;

        setGenHiddenWordsearchLoading(true);
        const result = await BedrockIdeaService.updateIdea(id, {
            genHiddenWordsearch: checked,
            ...(checked ? { genWordWheel: false, genStandaloneImage: false, genWordSearch9: false } : {}),
        });
        setGenHiddenWordsearchLoading(false);

        if (result.success) {
            if (result.data) {
                setFormData((prev) => ({
                    ...prev,
                    genHiddenWordsearch: Boolean(result.data.genHiddenWordsearch),
                    genWordWheel: Boolean(result.data.genWordWheel),
                    genStandaloneImage: Boolean(result.data.genStandaloneImage),
                    genWordSearch9: Boolean(result.data.genWordSearch9),
                }));
            }
            handleSnackBar(
                "success",
                checked
                    ? "Hidden Message / Word Search generation enabled"
                    : "Hidden Message / Word Search generation disabled"
            );
        } else {
            setFormData((prev) => ({ ...prev, genHiddenWordsearch: previous }));
            handleSnackBar("error", result.error || "Failed to update generation flag");
        }
    };

    const handleGenWordSearch9Change = async (event) => {
        const checked = event.target.checked;
        if (formData.genStandaloneImage && checked) {
            return;
        }
        const previous = formData.genWordSearch9;
        setFormData((prev) => ({
            ...prev,
            genWordSearch9: checked,
            genHiddenWordsearch: checked ? false : prev.genHiddenWordsearch,
            genWordWheel: checked ? false : prev.genWordWheel,
            genStandaloneImage: checked ? false : prev.genStandaloneImage,
        }));

        if (!isEdit) return;

        setGenWordSearch9Loading(true);
        const result = await BedrockIdeaService.updateIdea(id, {
            genWordSearch9: checked,
            ...(checked ? { genHiddenWordsearch: false, genWordWheel: false, genStandaloneImage: false } : {}),
        });
        setGenWordSearch9Loading(false);

        if (result.success) {
            if (result.data) {
                setFormData((prev) => ({
                    ...prev,
                    genWordSearch9: Boolean(result.data.genWordSearch9),
                    genHiddenWordsearch: Boolean(result.data.genHiddenWordsearch),
                    genWordWheel: Boolean(result.data.genWordWheel),
                    genStandaloneImage: Boolean(result.data.genStandaloneImage),
                }));
            }
            handleSnackBar(
                "success",
                checked ? "Word Search Mini generation enabled" : "Word Search Mini generation disabled"
            );
        } else {
            setFormData((prev) => ({ ...prev, genWordSearch9: previous }));
            handleSnackBar("error", result.error || "Failed to update Word Search Mini flag");
        }
    };

    const handleSnackBar = (sev, msg) => {
        setOpenSnackbar(true);
        setSeverity(sev);
        setMessage(msg);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validation = validateCronExpression(formData.cron);
        if (!validation.valid) {
            handleSnackBar("error", validation.error);
            return;
        }
        if (!formData.title.trim()) {
            handleSnackBar("error", "Title is required");
            return;
        }
        (async () => {
            const result = isEdit
                ? await BedrockIdeaService.updateIdea(id, formData)
                : await BedrockIdeaService.createIdea(formData);
            if (result.success) {
                navigate("/admin/bedrock-idea", {
                    state: { message: isEdit ? "Idea updated successfully" : "Idea created successfully" }
                });
            } else {
                handleSnackBar("error", result.error);
            }
        })();
    };

    const handleCancel = () => {
        navigate("/admin/bedrock-idea");
    };

    const handleDeleteClick = () => {
        setOpenConfirmDialog(true);
    };

    const handleConfirmDelete = async (ideaId) => {
        const result = await BedrockIdeaService.deleteIdea(ideaId);
        setOpenConfirmDialog(false);
        if (result.success) {
            navigate("/admin/bedrock-idea", { state: { message: "Idea deleted successfully" } });
        } else {
            handleSnackBar("error", result.error);
        }
    };

    if (loading) {
        return (
            <Box sx={{ flexGrow: 1, p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <IconButton aria-label="Back to list" onClick={() => navigate("/admin/bedrock-idea")} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography>Loading...</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                <IconButton
                    aria-label="Back to list"
                    onClick={() => navigate("/admin/bedrock-idea")}
                    size="small"
                    sx={{ mr: 0.5 }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                    {isEdit ? "Edit Bedrock Idea" : "New Bedrock Idea"}
                </Typography>
            </Box>

            {isEdit ? (
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                    <Tab label="Topics" />
                    <Tab label="Idea" />
                    <Tab label="Log" />
                </Tabs>
            ) : null}

            {isEdit && tabValue === 0 && (
                <BrTabTopics bedrockIdeaId={id} onError={handleSnackBar} />
            )}

            {isEdit && tabValue === 2 && (
                <BrTabLog bedrockIdeaId={id} onError={handleSnackBar} />
            )}

            {(!isEdit || tabValue === 1) && (
                <BrTabIdea
                    formData={formData}
                    onFormChange={handleFormChange}
                    onGenHiddenWordsearchChange={handleGenHiddenWordsearchChange}
                    onGenWordWheelChange={handleGenWordWheelChange}
                    onGenStandaloneImageChange={handleGenStandaloneImageChange}
                    onGenWordSearch9Change={handleGenWordSearch9Change}
                    genHiddenWordsearchLoading={genHiddenWordsearchLoading}
                    genWordWheelLoading={genWordWheelLoading}
                    genStandaloneImageLoading={genStandaloneImageLoading}
                    genWordSearch9Loading={genWordSearch9Loading}
                    onCronChange={(cron) => setFormData((prev) => ({ ...prev, cron }))}
                    onTagsChange={(event, newTags) => setFormData((prev) => ({ ...prev, tags: newTags }))}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    onDelete={isEdit ? handleDeleteClick : undefined}
                    isEdit={isEdit}
                    availableTags={availableTags}
                    loadingTags={loadingTags}
                    qualityStats={qualityStats}
                />
            )}

            {isEdit && (
                <ConfirmDialog
                    open={openConfirmDialog}
                    setOpen={setOpenConfirmDialog}
                    title="Delete Bedrock Idea"
                    message="Are you sure you want to delete this bedrock idea?"
                    warningAlert="Related system logs will not be deleted, but they will no longer be linked to this idea."
                    targetName={formData.title}
                    targetId={id}
                    callback={handleConfirmDelete}
                />
            )}

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />
        </Box>
    );
}
